import React, { Component } from 'react';
import moment from 'moment';
import actions from 'actions';
import utils from 'utils';
import LoadingPage from 'components/LoadingPage';
import './styles.scss';

const getQueueTelemetryProperties = metadata => {
  const count = metadata?.count;
  const ageSampleCount = metadata?.ageSampleCount;
  const oldestItemAt = metadata?.oldestItemAt;
  const numericCount = count === null || typeof count === 'undefined' ? null : Number(count);
  const numericAgeSampleCount =
    ageSampleCount === null || typeof ageSampleCount === 'undefined'
      ? null
      : Number(ageSampleCount);
  const numericOldestItemAt =
    oldestItemAt === null || typeof oldestItemAt === 'undefined'
      ? null
      : Number(oldestItemAt);
  const now = Date.now();

  return {
    ...(Number.isInteger(numericCount) && numericCount >= 0
      ? { queue_count: numericCount }
      : {}),
    ...(Number.isInteger(numericAgeSampleCount) && numericAgeSampleCount >= 0
      ? { queue_age_sample_count: numericAgeSampleCount }
      : {}),
    ...(Number.isFinite(numericOldestItemAt) &&
    numericOldestItemAt > 0 &&
    numericOldestItemAt <= now
      ? { oldest_item_age_seconds: Math.round((now - numericOldestItemAt) / 1000) }
      : {}),
  };
};

class Hydrate extends Component {
  telemetry = null;
  photoShownAt = new Map();
  decisionAttempts = new Map();
  hydratingPhotoIds = new Set();

  state = {
    callsInProgress: 0,
    remainingLimit: {
      limit: null,
      when: null,
    },
    isLoading: true,
    seeds: [],
    unhydrated: [],
    lastResponse: null,
    hydrateInProgress: 0,
  };

  constructor(props) {
    super(props);

    const limit = utils.storage.local.get('remainingLimit');

    if (limit) {
      this.state.remainingLimit = limit;
    }
  }

  componentDidMount() {
    this.telemetry = actions.moderationTelemetry.startSession('hydration');
    this.getUnhydrated();
  }

  componentWillUnmount() {
    this.telemetry?.stop('unmount');
  }

  getUnhydrated = () => {
    const loadStartedAt = Date.now();
    this.setState({ isLoading: true });

    actions.photos
      .getUnhydrated(300)
      .then(({ photos: unhydrated, queue }) => {
        this.telemetry?.track('MODERATION_QUEUE_LOADED', {
          queue: 'hydration',
          stage: 'queue',
          load_succeeded: true,
          load_duration_ms: Date.now() - loadStartedAt,
          ...getQueueTelemetryProperties(queue),
        });
        this.setState({ unhydrated, isLoading: false, lastResponse: unhydrated });
      })
      .catch(error => {
        this.telemetry?.track('MODERATION_QUEUE_LOAD_FAILED', {
          queue: 'hydration',
          stage: 'queue',
          load_succeeded: false,
          load_duration_ms: Date.now() - loadStartedAt,
          error_type: actions.moderationTelemetry.classifyError(error),
        });
        this.setState({
          isLoading: false,
          lastResponse: {
            status: 'error',
            message: error.message,
          },
        });
      });
  };

  updateRemainingLimit = limit => {
    const remainingLimit = {
      limit: limit,
      when: Date.now(),
    };

    utils.storage.local.set('remainingLimit', remainingLimit);
    this.setState({ remainingLimit });
  };

  trackPhotoShown = photo => {
    this.photoShownAt.set(photo.id, Date.now());
    this.telemetry?.track('MODERATION_PHOTO_SHOWN', {
      queue: 'hydration',
      stage: 'hydrate',
      ai_evidence_available: false,
      nearby_evidence_evaluated: false,
      nearby_evidence_available: false,
    });
  };

  _hydrate = photo => {
    if (this.hydratingPhotoIds.has(photo.id)) {
      return;
    }

    if (!this.photoShownAt.has(photo.id)) {
      this.trackPhotoShown(photo);
    }

    this.hydratingPhotoIds.add(photo.id);
    const attemptNumber = Number(this.decisionAttempts.get(photo.id) || 0) + 1;
    const properties = {
      decision_id: actions.moderationTelemetry.createId(),
      queue: 'hydration',
      stage: 'hydrate',
      decision_type: 'hydrate',
      is_final_decision: false,
      decision_duration_seconds: Number(
        ((Date.now() - (this.photoShownAt.get(photo.id) || Date.now())) / 1000).toFixed(1)
      ),
      attempt_number: attemptNumber,
      retry_count: attemptNumber - 1,
      is_retry: attemptNumber > 1,
      ai_evidence_available: false,
      nearby_evidence_evaluated: false,
      nearby_evidence_available: false,
    };
    const requestStartedAt = Date.now();

    this.decisionAttempts.set(photo.id, attemptNumber);
    this.telemetry?.track('MODERATION_DECISION_ATTEMPTED', properties);

    this.setState(
      state => {
        return {
          unhydrated: state.unhydrated.filter(x => x.id !== photo.id),
          hydrateInProgress: state.hydrateInProgress + 1,
        };
      },
      () => {
        actions.photos
          .hydrate(photo)
          .then(response => {
            this.telemetry?.track('MODERATION_DECISION_SUCCEEDED', {
              ...properties,
              request_duration_ms: Date.now() - requestStartedAt,
            });
            this.decisionAttempts.delete(photo.id);
            this.photoShownAt.delete(photo.id);
            this.hydratingPhotoIds.delete(photo.id);
            this.updateRemainingLimit(response.remainingLimit);

            this.setState(
              state => ({
                hydrateInProgress: Math.max(0, state.hydrateInProgress - 1),
              }),
              () => {
                if (this.state.unhydrated.length === 0 && this.state.hydrateInProgress === 0) {
                  this.getUnhydrated();
                }
              }
            );
          })
          .catch(error => {
            this.telemetry?.track('MODERATION_DECISION_FAILED', {
              ...properties,
              request_duration_ms: Date.now() - requestStartedAt,
              error_type: actions.moderationTelemetry.classifyError(error),
            });
            this.hydratingPhotoIds.delete(photo.id);

            this.setState(state => ({
              unhydrated: state.unhydrated.some(item => item.id === photo.id)
                ? state.unhydrated
                : [photo, ...state.unhydrated],
              hydrateInProgress: Math.max(0, state.hydrateInProgress - 1),
              lastResponse: {
                status: 'error',
                message: error.message,
              },
            }));
          });
      }
    );
  };

  onHydrateAllClick = () => {
    let delay = 0;
    this.state.unhydrated.forEach(photo => {
      delay = delay + Math.random() * 200;

      setTimeout(() => {
        this._hydrate(photo);
      }, delay);
    });
  };

  render() {
    return (
      <div className="Hydrate content">
        {this.state.isLoading && <LoadingPage />}

        <div className="remainingLimit">
          Remaining Limit:{' '}
          <span className={'limit ' + (this.state.remainingLimit.limit < 10 ? '--warning' : '')}>
            {this.state.remainingLimit.limit}
          </span>{' '}
          <span className="when">({moment(this.state.remainingLimit.when).fromNow()})</span>
        </div>

        <h2>Hydrate ({this.state.hydrateInProgress})</h2>

        <button onClick={this.onHydrateAllClick}>
          Hydrate all ({this.state.unhydrated.length})
        </button>

        <table className="table">
          <tbody>
            <tr>
              <th>Photo</th>
              <th>Actions</th>
            </tr>

            <tr>
              <th className="table-text">Preview</th>
              <th>Hydrate</th>
            </tr>

            {[...this.state.unhydrated].map(photo => (
              <tr key={photo.id}>
                <td>
                  <img
                    data-id={photo.id}
                    src={photo.urls.small}
                    alt="Unhydrated"
                    onLoad={() => this.trackPhotoShown(photo)}
                  />
                </td>
                <td className="cta" onClick={() => this._hydrate(photo)}>
                  Hydrate
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="console">
          <pre>{JSON.stringify(this.state.lastResponse, null, 4)}</pre>
        </div>
      </div>
    );
  }
}

export default Hydrate;
